import { useCallback, useEffect, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { VOICE_SILENCE_TIMEOUT_MS } from '../constants/config';
import { startNativeDictation, stopNativeDictation } from '../lib/nativeSpeech';

// In a native iOS/macOS WebView the Web Speech API is unavailable, so dictation
// is routed through the native speech plugin (see lib/nativeSpeech.ts). On web /
// PWA this is false and everything below uses the Web Speech API as before.
const IS_NATIVE = Capacitor.isNativePlatform();

// ─── Types ─────────────────────────────────────────────────────────────────────

// Extend window with webkit-prefixed Speech API (Safari / older Chrome)
declare global {
  interface Window {
    SpeechRecognition?: typeof SpeechRecognition;
    webkitSpeechRecognition?: typeof SpeechRecognition;
  }
}

interface UseVoiceOptions {
  /** Called when the user finishes speaking (after silence timeout). */
  onTranscript?: (text: string) => void;
}

interface UseVoiceReturn {
  isListening: boolean;
  isSupported: boolean;
  transcript: string;         // Live interim transcript while speaking
  startListening: () => void;
  stopListening: () => void;
}

// ─── Hook ──────────────────────────────────────────────────────────────────────

export function useVoice({ onTranscript }: UseVoiceOptions = {}): UseVoiceReturn {
  // Check support once — Web Speech API on Chrome/Edge/Safari 14.5+, OR the
  // native plugin when running inside the Capacitor app.
  const isSupported =
    IS_NATIVE ||
    (typeof window !== 'undefined' &&
     (typeof window.SpeechRecognition !== 'undefined' ||
      typeof window.webkitSpeechRecognition !== 'undefined'));

  const [isListening, setIsListening]   = useState(false);
  const [transcript, setTranscript]     = useState('');

  const recognitionRef  = useRef<SpeechRecognition | null>(null);
  const silenceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const finalTextRef    = useRef(''); // accumulates final (non-interim) results
  const onTranscriptRef = useRef(onTranscript);

  // Keep callback ref up to date without recreating the hook
  useEffect(() => {
    onTranscriptRef.current = onTranscript;
  }, [onTranscript]);

  // ─── Reset silence timer ────────────────────────────────────────────────────
  //
  // Called on every speech result. If no new result arrives within the timeout,
  // we treat the speech as finished and call onTranscript.

  const resetSilenceTimer = useCallback(() => {
    if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);

    silenceTimerRef.current = setTimeout(() => {
      const text = finalTextRef.current.trim();
      if (text && onTranscriptRef.current) {
        onTranscriptRef.current(text);
      }
      // Stop recognition after submitting
      if (IS_NATIVE) {
        void stopNativeDictation();
        setIsListening(false);
      } else if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    }, VOICE_SILENCE_TIMEOUT_MS);
  }, []);

  // ─── Start listening ─────────────────────────────────────────────────────────

  const startListening = useCallback(() => {
    if (!isSupported) return;
    if (isListening) return;

    // Native (iOS/macOS app): use the SFSpeechRecognizer plugin. Partial results
    // arrive as the FULL running transcript, so we replace (not append).
    if (IS_NATIVE) {
      finalTextRef.current = '';
      setTranscript('');
      setIsListening(true);
      startNativeDictation({
        onPartial: (text) => {
          finalTextRef.current = text;
          setTranscript(text);
          resetSilenceTimer();
        },
      }).catch((err) => {
        console.error('[Voice] native dictation failed:', err);
        setIsListening(false);
      });
      return;
    }

    const SpeechRecognitionAPI =
      window.SpeechRecognition ?? window.webkitSpeechRecognition;

    if (!SpeechRecognitionAPI) return;

    const recognition = new SpeechRecognitionAPI();
    recognitionRef.current = recognition;
    finalTextRef.current = '';

    // Configuration
    recognition.lang = 'en-US'; // can be made configurable later
    recognition.interimResults = true;  // show live transcript while speaking
    recognition.continuous = true;      // don't stop after first pause
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      console.log('[Voice] Recognition started');
      setIsListening(true);
      setTranscript('');
    };

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let interim = '';
      let finalChunk = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          finalChunk += result[0].transcript;
        } else {
          interim += result[0].transcript;
        }
      }

      if (finalChunk) {
        finalTextRef.current += finalChunk + ' ';
      }

      // Show live transcript (final so far + current interim)
      setTranscript(finalTextRef.current + interim);

      // Reset silence timer on every result
      resetSilenceTimer();
    };

    recognition.onspeechend = () => {
      // Speech stopped but recognition hasn't ended yet.
      // The silence timer will fire and call onTranscript.
      console.log('[Voice] Speech ended');
    };

    recognition.onend = () => {
      console.log('[Voice] Recognition ended');
      setIsListening(false);
      setTranscript('');
      finalTextRef.current = '';
      if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      console.error('[Voice] Recognition error:', event.error);
      // 'no-speech' is not a real error — user just didn't say anything
      if (event.error !== 'no-speech') {
        setIsListening(false);
      }
    };

    recognition.start();
  }, [isSupported, isListening, resetSilenceTimer]);

  // ─── Stop listening ──────────────────────────────────────────────────────────

  const stopListening = useCallback(() => {
    if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);

    // Submit whatever we have so far (both engines accumulate into finalTextRef).
    const text = finalTextRef.current.trim();
    if (text && onTranscriptRef.current) {
      onTranscriptRef.current(text);
    }

    if (IS_NATIVE) {
      void stopNativeDictation();
    } else if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }

    setIsListening(false);
    setTranscript('');
    finalTextRef.current = '';
  }, []);

  // ─── Cleanup on unmount ──────────────────────────────────────────────────────

  useEffect(() => {
    return () => {
      if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
      if (IS_NATIVE) {
        void stopNativeDictation();
      } else if (recognitionRef.current) {
        recognitionRef.current.abort(); // abort = stop without firing result
      }
    };
  }, []);

  return {
    isListening,
    isSupported,
    transcript,
    startListening,
    stopListening,
  };
}
